import {Component, Element, Event, type EventEmitter, h, Listen, Prop, State} from '@stencil/core';
import {GenericCardContainer} from '../../generic-subcomponents/generic-card-container/generic-card-container';
import type {SearchResultClickEvent} from '../../../search-tab/SearchResultClickEvent';
import {GenericCardEventsLayer} from '../../generic-subcomponents/generic-card-event-layer/generic-card-event-layer';
import type {SearchApiFaq} from '../../../../../api/model/response/documents/SearchResult';
import type {SearchProduct} from '../../../../../api/model/response/documents/public/Product';
import {makeLogger} from '../../../../../logger/logger';
import {ResultCardType} from '../product-card/helpers/helpers';
import {Strategies} from '../../../../strategies/Strategies';
import {isMobile} from '../../../../../utils/media';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';
import {GenericCardThumbnail} from '../../generic-subcomponents/generic-card-thumbnail/generic-card-thumbnail';
import {GenericCardTitle} from '../../generic-subcomponents/generic-card-title/generic-card-title';
import {GenericCardLabels} from '../../generic-subcomponents/generic-card-labels/generic-card-labels';
import {GenericCardDescription} from '../../generic-subcomponents/generic-card-description/generic-card-description';
import type { LabelListElement } from '../../../result-cards/subcomponents/uiaas-search-label-list';
import type { DateFormattingStrategyService } from '../../../../strategies/DateFormattingStrategyService';
import {DATE_FORMATTING} from '../../../../strategies/utils/variables';

const {info} = makeLogger({pathToCurrentFile: 'components/result-page/new-result-cards/uiaas-search-result-document-v1.tsx'});


@Component({
  tag: 'uiaas-search-result-faq-v2',
  shadow: true,
  styleUrl: 'uiaas-search-result-faq-v2.scss',
})

export class UiaasSearchResultFaqV2 {

    @Prop() readonly doc: SearchApiFaq;
    @Prop() readonly gridView: boolean;
    @Prop() readonly isDashboardView: boolean = false;
    @Prop() readonly srt: boolean = false;
    @Prop({mutable: true}) labelProvider?: labelProviderInterface;
    @Prop() readonly strategies: Strategies;

    /**
     * Position of search result in a list
     * (required for CTR-related analysis)
     */
    @Prop() readonly index: number = null;

    @Prop() readonly language: string;

    @Prop() readonly country: string;

    @Prop({ mutable: true, reflect: true, attribute: 'imageContain' }) imageContain = false; //eslint-disable-line @stencil-community/strict-mutable

    @State() accessibilityMode;

    @State() isDialogOpen = false;

    @State() isMobile = isMobile();

    @Listen('uiaas:accessibility', { target: 'window' })
    handleAccessibility(ev) {
        this.accessibilityMode = ev.detail;
    }

    @Element() el;

    @Event({
      eventName: 'search:ResultCardClick',
      composed: true,
      cancelable: true,
      bubbles: true
    })
      resultCardClickEventEmitter: EventEmitter<SearchResultClickEvent>;

    connectedCallback() {
        const htmlElement = document.querySelector('html');
        this.accessibilityMode = htmlElement.classList.contains('se2-accessibility');
    }

    componentWillLoad() {
        if (!this.labelProvider) {
          this.labelProvider = (name, fallback) => {
              info(
                'LabelProvider not provided in DocumentCardNew component',
                [name, fallback]
              );
              return fallback;
          };
        }
    }

    render() {
        function renderListWithSep(
          list: Array<string>,
          separator: string
        ): string {
            if (!list || list.length === 0) return '';
            return list?.flat().join(separator);
        }

        const dateFormatter:DateFormattingStrategyService = this.strategies.for(DATE_FORMATTING);
        const publishDate = dateFormatter.format(this.doc.publishDate, this.country, this.language);
        const modifyDate = dateFormatter.format(this.doc.modifyDate, this.country, this.language);
        const withRibbon = this.doc.access && this.doc.access == 'RESTRICTED';
        const categories = renderListWithSep(this.doc.relatedProducts ? this.doc.relatedProducts.concat(
          this.doc.relatedRanges) : this.doc.relatedRanges,', ');
        const moficicationDatesLabels: LabelListElement[] = [{
          label: this.labelProvider('result.faq.publishDate', 'Published on'),
          value: publishDate,
        },
        {
          label: this.labelProvider('result.faq.modifyDate', 'Last modified on'),
          value: modifyDate,
        }];
        const categoryLabel: LabelListElement[] = [{
          label: this.labelProvider('result.faq.productCategory', 'Product category'),
          value: categories
        }];
        return (
          <div>
            <GenericCardEventsLayer clickEmitter={this.resultCardClickEventEmitter} resutCardType={ResultCardType.Document}
              doc={this.doc as unknown as SearchProduct} index={this.index}>
              <GenericCardContainer gridView={this.gridView} isDashboardView={this.isDashboardView} doc={this.doc} srt={this.srt}
                labelProvider={(e, f) => this.labelProvider(e, f)} strategies={this.strategies}>
                <section class="result-card__image-container">
                  {this.doc.videoThumbnail ? <GenericCardThumbnail image={this.doc.videoThumbnail}/> : ''}
                  {this.doc.thumbnailUrl ? <GenericCardThumbnail image={this.doc.thumbnailUrl}/> : ''}
                </section>
                <section class={'product-result-card__text-content-section'}>
                  {withRibbon ? this.privateDocIconLabelRender() : ''}
                  <GenericCardTitle url={this.doc.url} text={this.doc.title} strategies={this.strategies}/>
                  <GenericCardDescription description={(this.doc as unknown as SearchApiFaq).description}/>
                  <GenericCardLabels labelProvider={(e, f) => this.labelProvider(e, f)} labels={moficicationDatesLabels}/>
                  <GenericCardLabels labelProvider={(e, f) => this.labelProvider(e, f)} labels={categoryLabel}/>
                </section>
              </GenericCardContainer>
            </GenericCardEventsLayer>
          </div>
        );
    }

    private privateDocIconLabelRender() {
        return <div class={'faq-premium-tag'}>
          <qds-tag
            text="Premium"
            status="success"
            size="small"
            icon-name="crown"
          />
        </div>;
    }
}
